import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import { useDispatch, useSelector } from 'react-redux';
import GenericLineChart from '../../Charts/GenericLineChart';
import Skeletons from '@/components/Skeletons/Skeletons';
import TrendChip from '../../TrendChip';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import {
  setPerformanceModals,
  toggleActiveLegend,
} from '@/store/slices/components/dashboard/DashboardSlice';
import { getPerformanceConcretedOperationsEvolutionCard } from '@/store/slices/components/dashboard/service';
import getLineChartYStepValue from '../../utils/getLineChartYStepValue';
import { useDndCardContext } from '../../DndCard/context';
import styles from './concretedOperationsEvolution.module.scss';

export default function ConcretedOperationsEvolution() {
  const dispatch = useDispatch();
  const { edition } = useSelector((state) => state.DashboardSlice);
  const {
    loading,
    hasFetched,
    value,
    variation,
    percentage_status,
    icon,
    labels,
    datasets,
    activeLegends,
  } = useSelector((state) => state.DashboardSlice.concretedOperationsEvolution);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const { visible: dndVisible } = useDndCardContext();

  useFetchOnViewport(
    (query) => {
      dispatch(getPerformanceConcretedOperationsEvolutionCard({ query }));
    },
    [],
    { hasFetched }
  );

  const handleOpenTable = () => {
    dispatch(
      setPerformanceModals({
        key: 'concretedOperations',
        subkey: 'isOpen',
        value: true,
      })
    );
  };

  const hasChartData = datasets?.length && labels?.length;
  const filteredDatasets = datasets.filter((dataset) =>
    activeLegends.includes(dataset.id)
  );

  const renderSkeletons = () => (
    <>
      <Skeletons height={24} width={180} />
      <Skeletons height={24} width={90} className={styles.skeletonChip} />
      <Skeletons height={240} width="100%" />
    </>
  );

  return (
    <Box ref={ref} id="concreted-operations-evolution" className={styles.card}>
      {loading || isStale ? (
        renderSkeletons()
      ) : (
        <>
          <Box className={styles.header}>
            <Box className={styles.headerLeft}>
              {edition.active && dndVisible && (
                <Icon
                  className={styles.hiddenIcon}
                  icon="icon-menu"
                  fontSize="12px"
                />
              )}
              <Text className={styles.title}>
                {Trans(translations, 'ConcretedOperations')}
              </Text>
            </Box>
            {!edition.active && (
              <Icon
                icon="icon-tabla"
                className={styles.tableIcon}
                onClick={handleOpenTable}
              />
            )}
          </Box>

          <Box className={styles.summary}>
            <Box className={styles.summaryText}>
              <Text className={styles.value} title={value}>
                {value}
              </Text>
              <TrendChip
                text={variation}
                icon={icon}
                percentageStatus={percentage_status}
              />
            </Box>
          </Box>

          <Box className={styles.legendsWrapper}>
            {datasets.map((dataset) => (
              <Box
                key={dataset.label}
                className={styles.legend}
                onClick={() => dispatch(toggleActiveLegend(dataset.id))}
                data-disabled={!activeLegends.includes(dataset.id)}
              >
                <Box
                  className={styles.legendColorBox}
                  __css={{ backgroundColor: dataset.borderColor }}
                />
                <Text className={styles.legendText}>{dataset.label}</Text>
              </Box>
            ))}
          </Box>

          <Box className={styles.chart}>
            {hasChartData && (
              <GenericLineChart
                labels={labels}
                datasets={filteredDatasets}
                yStepSize={getLineChartYStepValue(filteredDatasets[0]?.data)}
                autoSkip
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
